import React, { useRef, useState, useEffect } from "react";

import useIsAr from "../../Hooks/useIsAr";
import Arc from "./Arc";
import Pointer from "./Pointer";
import TextLayer from "./TextLayer";

import "./style.scss";

function Gauge({
  progress = 0,
  colors = [],
  color,
  width,
  statusColor,
  hasNoTargetBaseline = false
}) {
  const [radius, setRadius] = useState(null);
  const [isAr] = useIsAr();
  const containerRef = useRef();

  useEffect(() => {
    if (!radius) {
      setRadius(containerRef?.current?.offsetWidth / 2.5);
    }
    setRadius(containerRef?.current?.offsetWidth / 2.5);
    window.addEventListener("resize", () => {
      setRadius(containerRef?.current?.offsetWidth / 2.5);
    });
    return () => {
      window.removeEventListener("resize", () => {
        setRadius(containerRef?.current?.offsetWidth / 2.5);
      });
    };
  }, []);

  let progressRanged = progress > 0 ? progress * 0.95 : 0;
  const progressRangedDegree = Math.min(
    Math.max((progressRanged / 100) * 180, 0),
    180
  );

  function getMaxValue(val) {
    if (val !== null) {
      return val * 0.95;
    }
    return 100;
  }

  function getMinValue(val) {
    if (val === 100) return 95;
    if (val !== null) return val * 0.95;
    return 0;
  }

  function getColors() {
    if (!colors?.length) return [{ min: 0, max: 100, color }];
    const colorsTrimmed = colors
      .filter((item) => item.Code !== "NA")
      .map((e) => ({
        min: getMinValue(e.Min),
        max: getMaxValue(e.Max),
        text: e.Min || 0,
        color: e.Color
      }));

    return colorsTrimmed;
  }

  const colorsEnhanced = getColors();

  return (
    <div
      ref={containerRef}
      className="gauge-container"
      style={{
        maxHeight: radius + 25,
        paddingBottom: radius * (1 / 16),
        width: width || "100%"
      }}
    >
      <div
        style={{
          width: radius * 2,
          height: radius,
          borderRadius: `${radius}px ${radius}px 0 0`,
          background: hasNoTargetBaseline ? statusColor : "#EFF3F7",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end"
        }}
      >
        <div
          style={{
            width: radius,
            height: radius / 2,
            borderRadius: `${radius / 2}px ${radius / 2}px 0 0`,
            background: "white",
            zIndex: 1
          }}
        />
        <div
          style={{
            width: radius * 2,
            height: radius,
            borderRadius: `${radius}px ${radius}px 0 0`,
            background: `conic-gradient(
          from -90deg
          at 50% 100%,
             ${colorsEnhanced
               .map(
                 (item) =>
                   `${hasNoTargetBaseline ? statusColor : item.color} ${
                     (item.max / 100) * 180 > progressRangedDegree
                       ? progressRangedDegree
                       : (item.max / 100) * 180
                   }deg`
               )
               .join(" ,")}
          ,transparent ${progressRangedDegree}deg)`,
            position: "absolute"
          }}
        />
      </div>
      {colorsEnhanced?.length > 1 && (
        <>
          <div
            className="gauge-container__upper-lines"
            style={{ left: -radius, top: -7 }}
          >
            {colorsEnhanced?.map((e) => (
              <Arc
                radius={radius + 10}
                width={e.max - e.min - 0.25}
                start={e.min}
                color={hasNoTargetBaseline ? statusColor : e.color}
              />
            ))}
          </div>
          <TextLayer list={colorsEnhanced} radius={radius + 33} />
        </>
      )}
      <Pointer degree={progressRangedDegree} radius={radius} />
    </div>
  );
}

export default Gauge;
